import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Button } from "../../components/common/Button";
import {
  FormContainer,
  FormHeading,
  FormText,
} from "../../components/common/Form";
import * as SentryUtils from "../../utils/sentry";
import { formatShopPrice } from "./catalogue";
import {
  fetchShopOrderBySessionId,
  fetchShopPaymentStatus,
  type ShopOrderWithItems,
} from "./shop.service";
import { SHOP_ORDERS_PATH } from "./ShopOrders";

const SuccessIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      fill="currentColor"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      fill="currentColor"
    />
  </svg>
);

const ContentContainer = styled.div`
  text-align: center;
  margin: 48px 0;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
`;

// The same summary box the buyer saw in the cart, so the order reads the same.
const Summary = styled.div`
  border: 1px solid #000;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  padding: 24px;
  text-align: left;
`;

const SummaryTitle = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
`;

const SummaryRow = styled.div`
  color: #000;
  display: flex;
  font-size: 14px;
  gap: 12px;
  justify-content: space-between;
  line-height: 1.2;
  text-transform: uppercase;
`;

const SummaryDivider = styled.div`
  background: #000;
  height: 0.5px;
`;

const SummaryTotalRow = styled(SummaryRow)`
  font-size: 20px;
  font-weight: 700;
`;

const PickupNotice = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
  text-transform: uppercase;
`;

const POLL_INTERVAL_MS = 2000;

type CheckoutStatus = "loading" | "success" | "error";

/**
 * Where Stripe sends the buyer back. Stripe confirms the payment before our
 * webhook creates the order, so the page waits for both in turn: first the
 * session status, then the order itself. Neither wait has a time limit: if a
 * step never completes, the buyer keeps seeing it rather than a guess.
 */
export const ShopConfirm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CheckoutStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [order, setOrder] = useState<ShopOrderWithItems | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setErrorMessage("Sessione di pagamento mancante");
      setStatus("error");
      return;
    }

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const reportError = (error: unknown, operation: string) => {
      SentryUtils.captureException(error, {
        tags: { component: "ShopConfirm", operation },
        extra: { sessionId },
      });
      console.error("Error confirming shop checkout:", error);
    };

    const waitForOrder = async () => {
      try {
        const confirmedOrder = await fetchShopOrderBySessionId(sessionId);

        if (isCancelled) {
          return;
        }

        if (confirmedOrder) {
          setOrder(confirmedOrder);
          return;
        }

        timeoutId = setTimeout(waitForOrder, POLL_INTERVAL_MS);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        reportError(error, "shop_order_loading");
        setOrderError(
          error instanceof Error
            ? error.message
            : "Non è stato possibile caricare l'ordine",
        );
      }
    };

    const waitForPayment = async () => {
      try {
        const paymentStatus = await fetchShopPaymentStatus(sessionId);

        if (isCancelled) {
          return;
        }

        if (paymentStatus === "complete") {
          setStatus("success");
          void waitForOrder();
          return;
        }

        if (paymentStatus === "open") {
          timeoutId = setTimeout(waitForPayment, POLL_INTERVAL_MS);
          return;
        }

        reportError(
          new Error(`Shop checkout session is ${paymentStatus}`),
          "payment_status_verification",
        );
        setErrorMessage("La sessione di pagamento è scaduta");
        setStatus("error");
      } catch (error) {
        if (isCancelled) {
          return;
        }

        reportError(error, "payment_status_verification");
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Non è stato possibile verificare il pagamento",
        );
        setStatus("error");
      }
    };

    void waitForPayment();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [sessionId]);

  if (status === "loading") {
    return (
      <FormContainer>
        <ContentContainer>
          <FormHeading>VERIFICA PAGAMENTO IN CORSO</FormHeading>
          <FormText>ATTENDERE PREGO...</FormText>
        </ContentContainer>
      </FormContainer>
    );
  }

  if (status === "success") {
    return (
      <FormContainer>
        <ContentContainer>
          <IconWrapper>
            <SuccessIcon />
          </IconWrapper>
          <FormHeading>PAGAMENTO COMPLETATO</FormHeading>
          <FormText>
            IL TUO PAGAMENTO È STATO PROCESSATO CON SUCCESSO. RICEVERAI UNA MAIL
            CON IL RIEPILOGO DELL'ORDINE E LE ISTRUZIONI DI RITIRO.
          </FormText>
          {orderError && (
            <FormText>
              NON È STATO POSSIBILE CARICARE L'ORDINE: {orderError}
            </FormText>
          )}
          {!order && !orderError && (
            <FormText>REGISTRAZIONE DELL'ORDINE IN CORSO...</FormText>
          )}
          {order && (
            <Summary>
              <SummaryTitle>Il tuo ordine</SummaryTitle>
              {order.shop_order_items.map((item) => (
                <SummaryRow key={item.id}>
                  <span>
                    {item.product_name} · {item.variant_label}
                  </span>
                  <span>
                    {item.quantity} × {formatShopPrice(item.unit_price_cents)}
                  </span>
                </SummaryRow>
              ))}
              <SummaryDivider />
              <SummaryTotalRow>
                <span>Totale</span>
                <span>{formatShopPrice(order.total_cents)}</span>
              </SummaryTotalRow>
              <PickupNotice>
                Non è prevista spedizione. Per il ritiro contattaci
                telefonicamente oppure presso il negozio DaiFradei di William e
                Fabio a Gavardo.
              </PickupNotice>
            </Summary>
          )}
          <ButtonWrapper>
            <Button onClick={() => navigate(SHOP_ORDERS_PATH)} fullWidth>
              VEDI I MIEI ORDINI
            </Button>
            <Button
              onClick={() => navigate("/shop")}
              variant="outline"
              fullWidth
            >
              TORNA ALLO SHOP
            </Button>
          </ButtonWrapper>
        </ContentContainer>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <ContentContainer>
        <IconWrapper>
          <ErrorIcon />
        </IconWrapper>
        <FormHeading>ERRORE NEL PAGAMENTO</FormHeading>
        <FormText>
          SI È VERIFICATO UN ERRORE DURANTE IL PROCESSO DI PAGAMENTO. RIPROVA O
          CONTATTA L'ASSISTENZA SE IL PROBLEMA PERSISTE.
        </FormText>
        {errorMessage && <FormText>{errorMessage}</FormText>}
        <ButtonWrapper>
          <Button onClick={() => navigate("/shop")} fullWidth>
            TORNA ALLO SHOP
          </Button>
        </ButtonWrapper>
      </ContentContainer>
    </FormContainer>
  );
};
